import React, { useEffect, useMemo, useState } from "react";
import "./vendor.css";
import { fetchVendorMeta, registerVendor } from "../../api/vendor.api";

const initialFormState = {
  firstName: "",
  lastName: "",
  mobile: "",
  email: "",
  address: "",
  state: "",
  city: "",
  gst: "",
  storeUrl: "",
  salesChannel: "",
  quantity: "",
  brands: [],
  agreeTerms: true,
  receiveUpdates: false,
};


const VendorIntakeForm = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [meta, setMeta] = useState({
    states: [],
    brands: [],
    monthly_quantities: [],
    sales_channels: [],
  });

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    fetchVendorMeta()
      .then((data) => {
        if (!mounted) return;
        setMeta({
          states: data.states || [],
          brands: data.brands || [],
          monthly_quantities: data.monthly_quantities || [],
          sales_channels: data.sales_channels || [],
        });
      })
      .catch(() => {
        if (mounted) {
          setMeta({
            states: [],
            brands: [],
            monthly_quantities: [],
            sales_channels: [],
          });
        }
      });

    return () => {
      mounted = false;
    };
  }, []);


  const selectedState = useMemo(
    () => meta.states.find((s) => s.id === Number(formData.state)),
    [meta.states, formData.state]
  );

  const cities = selectedState?.cities || [];


  const isValidMobile = (mobile) => /^\d{10}$/.test(mobile);
  const isValidGST = (gst) =>
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleBrand = (brandId) => {
    setFormData((prev) => ({
      ...prev,
      brands: prev.brands.includes(brandId)
        ? prev.brands.filter((id) => id !== brandId)
        : [...prev.brands, brandId],
    }));
  };

  const validateForm = () => {
    if (
      !formData.firstName ||
      !formData.email ||
      !formData.mobile ||
      !formData.address ||
      !formData.state ||
      !formData.city ||
      !formData.salesChannel ||
      !formData.quantity
    ) {
      return "Please fill all required fields.";
    }

    if (!isValidMobile(formData.mobile)) {
      return "Mobile number must be exactly 10 digits.";
    }
    if (!formData.gst) {
      return "GST number is required.";
    }

    if (!isValidGST(formData.gst)) {
      return "Please enter a valid 15-digit GST number.";
    }

    if (formData.brands.length === 0) {
      return "Please select at least one preferred brand.";
    }

    if (!formData.agreeTerms) {
      return "You must agree to the terms and conditions.";
    }

    if (!formData.storeUrl) {
      return "Store Website / Social Media URL is required.";
    }

    return null;
  };


  const handleRegister = async (e) => {
    e.preventDefault();
    if (loading) return;

    setErrorMsg("");
    setSuccessMsg("");

    const validationError = validateForm();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone_number: formData.mobile,
        gst_number: formData.gst,
        store_url: formData.storeUrl,
        address: formData.address,
        state: formData.state,
        city: formData.city,
        brands_interested: formData.brands,
        sales_channel: formData.salesChannel,
        montly_quantity_range: formData.quantity,
        agree_terms: formData.agreeTerms,
        notify_me: formData.receiveUpdates,
      };

      const res = await registerVendor(payload);
      setSuccessMsg(res?.message || "Vendor registered successfully");
      setFormData(initialFormState);
    } catch (err) {
      setErrorMsg(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="vendor">

      <div className="breadcrumb11">
        <div className="page-container">
          <span>Home</span>{" "}
          <span className="highlight"> / </span>{" "}
          <span>Vendor Partner</span>
        </div>

      </div>


      <div className="vendor__container">
        {successMsg && (
          <div className="vendor__alert vendor__alert--success">
            <span className="vendor__alert-icon">✔</span>
            <span>{successMsg}</span>
          </div>
        )}

        {errorMsg && (
          <div className="vendor__alert vendor__alert--error">
            <span className="vendor__alert-icon">⚠</span>
            <span>{errorMsg}</span>
          </div>
        )}

        <header className="vendor__header">
          <h1 className="vendor__title">
            VENDOR PARTNER <span className="vendor__title-highlight">INTAKE FORM</span>
          </h1>
          <p className="vendor__subtitle">
            Join our network of trusted vendors
          </p>
        </header>

        {/* PERSONAL DETAILS */}
        <section className="vendor__section">
          <h2 className="vendor__section-title">PERSONAL DETAILS</h2>

          <div className="vendor__row">
            <input
              name="firstName"
              placeholder="First Name *"
              className="vendor__input"
              value={formData.firstName}
              onChange={handleInputChange}
            />
            <input
              name="lastName"
              placeholder="Last Name"
              className="vendor__input"
              value={formData.lastName}
              onChange={handleInputChange}
            />
          </div>

          <div className="vendor__row">
            <input
              name="mobile"
              placeholder="Mobile *"
              className="vendor__input"
              value={formData.mobile}
              onChange={handleInputChange}
            />
            <input
              name="email"
              placeholder="Email *"
              className="vendor__input"
              value={formData.email}
              onChange={handleInputChange}
            />
          </div>
          <div className="vendor__row">
            <input
              name="gst"
              placeholder="GST Number *"
              className="vendor__input"
              value={formData.gst}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  gst: e.target.value.toUpperCase(),
                }))
              }
            />
          </div>
          <div className="vendor__row">
            <input
              name="storeUrl"
              placeholder="Paste your Store Website / FB page / Instagram URL *"
              className="vendor__input"
              value={formData.storeUrl}
              onChange={handleInputChange}
            />
          </div>


          <textarea
            name="address"
            placeholder="Address *"
            className="vendor__input vendor__textarea"
            value={formData.address}
            onChange={handleInputChange}
          />
        </section>

        {/* LOCATION */}
        <section className="vendor__section">
          <div className="vendor__row">
            <select
              name="state"
              className="vendor__select"
              value={formData.state}
              onChange={(e) => {
                handleInputChange(e);
                setFormData((p) => ({ ...p, city: "" }));
              }}
            >
              <option value="">Select State *</option>
              {meta.states.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>

            <select
              name="city"
              className="vendor__select"
              value={formData.city}
              onChange={handleInputChange}
              disabled={!formData.state}
            >
              <option value="">Select City *</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </section>

        {/* SALES */}
        <section className="vendor__section">
          <div className="vendor__row">
            <select
              name="salesChannel"
              className="vendor__select"
              value={formData.salesChannel}
              onChange={handleInputChange}
            >
              <option value="">Sales Channel *</option>
              {meta.sales_channels.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>

            <select
              name="quantity"
              className="vendor__select"
              value={formData.quantity}
              onChange={handleInputChange}
            >
              <option value="">Monthly Quantity *</option>
              {meta.monthly_quantities.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.min_value} – {q.max_value}
                </option>
              ))}
            </select>
          </div>

          <div className="vendor__checkbox-grid">
            {meta.brands.map((b) => (
              <label
                key={b.id}
                className={`vendor__checkbox ${formData.brands.includes(b.id) ? "is-checked" : ""
                  }`}
              >
                <input
                  type="checkbox"
                  checked={formData.brands.includes(b.id)}
                  onChange={() => toggleBrand(b.id)}
                />
                <span>{b.name}</span>
              </label>
            ))}
          </div>
        </section>

        {/* CONSENT */}
        <section className="vendor__section">
          <label className="vendor__radio">
            <input
              type="checkbox"
              checked={formData.agreeTerms}
              onChange={() =>
                setFormData((p) => ({ ...p, agreeTerms: !p.agreeTerms }))
              }
            />
            <span>I agree to Terms & Conditions *</span>
          </label>

          <label className="vendor__radio">
            <input
              type="checkbox"
              checked={formData.receiveUpdates}
              onChange={() =>
                setFormData((p) => ({
                  ...p,
                  receiveUpdates: !p.receiveUpdates,
                }))
              }
            />
            <span>Receive product updates</span>
          </label>
        </section>



        <div className="vendor__submit">
          <button
            className="vendor__button"
            onClick={handleRegister}
            disabled={loading}
          >
            {loading ? "Registering..." : "Register as Vendor"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorIntakeForm;
